/**
 * Game Session Context Provider
 * 
 * Manages game session state and provides API methods for game actions.
 * Integrates with WebSocket for real-time updates.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { gameApiService, GameSession, GameRound, PayoutLadder, TickerResult } from '../services/GameApiService';
import { bettingWebSocketService, GameSessionMessage, PriceUpdate } from '../services/BettingWebSocketService';
import { useWallet } from './WalletProvider';
import { useGameTransactions } from '../hooks/useGameTransactions';

// Race matchup interface for UI display
interface RaceMatchup {
  champion: string;
  challenger: string;
  championPrice: number;
  challengerPrice: number;
  championPercentGain: number;
  challengerPercentGain: number;
  startingPrices?: { champion: number; challenger: number };
  isLive: boolean; // true if session active and racing
}

interface GameSessionContextType {
  // State
  currentSession: GameSession | null;
  currentRound: GameRound | null;
  payoutLadder: PayoutLadder | null;
  matchupHistory: any;
  recentResults: TickerResult[]; // Activity ticker data
  isLoading: boolean;
  sessionCheckComplete: boolean;
  error: string | null;
  
  // Race data (ALWAYS available for display)
  raceMatchup: RaceMatchup | null;
  allPrices: { [symbol: string]: { price: number; timestamp: number } };
  
  // Actions
  createSession: (initialBet: number) => Promise<void>;
  pickAsset: (asset: string) => Promise<void>;
  cashOut: () => Promise<void>;
  continueGame: () => Promise<void>;
  checkActiveSession: () => Promise<void>;
  clearError: () => void;
  clearSessionState: () => void;
}

const GameSessionContext = createContext<GameSessionContextType | undefined>(undefined);

export const GameSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { address, isAuthenticated, isReady, solanaWallet } = useWallet();
  
  // Initialize game transactions hook
  const { 
    placeBet: placeBetOnChain, 
    continueStreak: continueStreakOnChain,
    cashOut: cashOutOnChain 
  } = useGameTransactions(solanaWallet);
  
  const [currentSession, setCurrentSession] = useState<GameSession | null>(null);
  const [currentRound, setCurrentRound] = useState<GameRound | null>(null);
  const [previousRoundResults, setPreviousRoundResults] = useState<any>(null); // Store signed_results from previous round for continue_streak
  const [payoutLadder, setPayoutLadder] = useState<PayoutLadder | null>(null);
  const [matchupHistory, setMatchupHistory] = useState<any>(null);
  const [recentResults, setRecentResults] = useState<TickerResult[]>([]); // Activity ticker data
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Start as false - will be set to true after wallet is ready AND session check completes
  const [sessionCheckComplete, setSessionCheckComplete] = useState(false);
  
  // ✅ Transaction lock to prevent double-clicks and race conditions
  const [transactionInProgress, setTransactionInProgress] = useState(false);
  
  // Track live prices for all assets (from WebSocket price updates)
  const [allPrices, setAllPrices] = useState<{ [symbol: string]: { price: number; timestamp: number } }>({});
  
  // Demo mode baseline prices for calculating live % changes
  const [demoBaseline, setDemoBaseline] = useState<{
    timestamp: number;
    prices: Record<string, number>;
  } | null>(null);

  // Load payout ladder on mount (static data)
  useEffect(() => {
    const loadPayoutLadder = async () => {
      try {
        const result = await gameApiService.getPayoutLadder();
        
        if (result.success && result.data) {
          setPayoutLadder(result.data);
        } else {
          console.error('Failed to load payout ladder:', result.error);
        }
      } catch (error) {
        console.error('Error loading payout ladder:', error);
      }
    };
    
    loadPayoutLadder();
  }, []);

  // Load recent results for activity ticker (refresh every 30 seconds)
  useEffect(() => {
    const loadRecentResults = async () => {
      try {
        const result = await gameApiService.getRecentResults(100);
        
        if (result.success && result.data) {
          // Shuffle results for variety each time we fetch
          const shuffled = [...result.data].sort(() => Math.random() - 0.5);
          setRecentResults(shuffled);
        } else {
          console.error('Failed to load recent results:', result.error);
        }
      } catch (error) {
        console.error('Error loading recent results:', error);
      }
    };
    
    // Load immediately
    loadRecentResults();
    
    // Refresh every 30 seconds
    const interval = setInterval(loadRecentResults, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // NEW: Subscribe to price updates from WebSocket
  // FALLBACK: Also subscribe to round_update messages to extract current prices
  useEffect(() => {
    const handlePriceUpdate = (priceUpdate: PriceUpdate) => {
      const newPrices: { [symbol: string]: { price: number; timestamp: number } } = {};
      
      priceUpdate.data.forEach(priceData => {
        newPrices[priceData.symbol] = {
          price: priceData.price,
          timestamp: priceData.timestamp
        };
      });
      
      setAllPrices(prevPrices => ({
        ...prevPrices,
        ...newPrices
      }));
    };
    
    bettingWebSocketService.addPriceUpdateCallback(handlePriceUpdate);
    
    return () => {
      bettingWebSocketService.removePriceUpdateCallback(handlePriceUpdate);
    };
  }, []);

  // Set demo baseline when entering demo mode (no active session)
  useEffect(() => {
    const hasEnoughPrices = Object.keys(allPrices).length >= 2;
    const now = Date.now();
    const isBaselineStale = demoBaseline && (now - demoBaseline.timestamp) > 5 * 60 * 1000; // 5 minutes
    
    // Set or refresh baseline in demo mode
    if (!currentSession && hasEnoughPrices && (!demoBaseline || isBaselineStale)) {
      setDemoBaseline({
        timestamp: now,
        prices: Object.fromEntries(
          Object.entries(allPrices).map(([symbol, data]) => [symbol, data.price])
        )
      });
    } 
    // Clear baseline when session starts
    else if (currentSession && demoBaseline) {
      setDemoBaseline(null);
    }
  }, [currentSession, allPrices, demoBaseline]);

  // Fetch matchup history when matchup changes (works for both demo and active session)
  useEffect(() => {
    const fetchMatchupHistory = async () => {
      let champion: string | undefined;
      let challenger: string | undefined;
      
      // Active session: use round data
      if (currentRound?.champion && currentRound?.challenger) {
        champion = currentRound.champion;
        challenger = currentRound.challenger;
      }
      // Demo mode: use default BTC vs SOL (hardcoded, not dependent on allPrices keys)
      else if (!currentSession) {
        champion = 'BTC';
        challenger = 'SOL';
      }
      
      if (!champion || !challenger) {
        setMatchupHistory(null);
        return;
      }

      try {
        const result = await gameApiService.getMatchupHistory(
          champion,
          challenger,
          10 // Get last 10 races
        );

        // Backend returns { success, history, matchup } directly (not wrapped in 'data')
        if (result.success && (result as any).history) {
          setMatchupHistory((result as any).history);
        } else {
          console.warn('⚠️ Matchup history fetch failed or no data:', result.error);
          setMatchupHistory(null);
        }
      } catch (error) {
        console.error('❌ Failed to load matchup history:', error);
        setMatchupHistory(null);
      }
    };

    fetchMatchupHistory();
    // Only refetch when champion/challenger actually change or session starts/ends
    // NOT when currentRound updates for other reasons (like player_pick)
    // NOT when allPrices updates (that happens every second!)
  }, [currentRound?.champion, currentRound?.challenger, currentSession?.session_id]);

  // Check for active session when wallet is ready
  // This ensures session is refetched even if logging in with the same wallet address
  useEffect(() => {
    // Don't do anything until wallet provider is ready
    if (!isReady) {
      return;
    }
    
    if (address && isAuthenticated) {
      setSessionCheckComplete(false);
      // Check for active session before showing UI
      checkActiveSession()
        .catch(err => {
          console.log('Note: Game session API not available yet:', err.message);
        })
        .finally(() => {
          setSessionCheckComplete(true);
        });
    } else {
      // Clear state when wallet disconnects OR user logs out (mark check as complete for demo mode)
      setCurrentSession(null);
      setCurrentRound(null);
      setError(null);
      setSessionCheckComplete(true); // ✅ Allow demo mode to show when not connected
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, isAuthenticated, isReady]); // ✅ Triggers on wallet ready + auth changes

  // WebSocket message handler for game session events
  useEffect(() => {
    const handleGameSessionMessage = (message: GameSessionMessage) => {
      // CRITICAL: Filter messages by wallet address first
      // Backend broadcasts to all clients, so we must filter on frontend
      if (address && message.data.userWallet && message.data.userWallet !== address) {
        return;
      }
      
      // Additional check: If we have a session, verify session ID matches
      if (currentSession && message.data.sessionId && message.data.sessionId !== currentSession.session_id) {
        return;
      }

      // Update session state from message (merge with existing session data)
      // CRITICAL: Only update fields that are explicitly provided in the message
      // This prevents resetting rounds_completed to 0 during betting/racing phases
      setCurrentSession(prev => {
        if (!prev && !message.data.sessionId) return null;
        
        const newRoundsCompleted = message.data.roundsCompleted !== undefined 
          ? message.data.roundsCompleted 
          : (prev?.rounds_completed ?? 0);
        
        return {
          session_id: message.data.sessionId || prev?.session_id || '',
          user_wallet: prev?.user_wallet || address || '',
          session_status: message.data.sessionStatus || prev?.session_status || 'active',
          rounds_completed: newRoundsCompleted,
          initial_bet: message.data.initialBet !== undefined
            ? message.data.initialBet
            : (prev?.initial_bet ?? 0),
          final_payout: message.data.finalPayout !== undefined
            ? message.data.finalPayout
            : prev?.final_payout,
          created_at: prev?.created_at || '',
          completed_at: message.data.sessionStatus !== 'active' ? new Date().toISOString() : prev?.completed_at
        };
      });
      // Update round state from message
      if (message.data.round) {
        const roundUpdate: GameRound = {
          round_id: message.data.round.roundId,
          session_id: message.data.sessionId,
          round_number: message.data.round.roundNumber,
          phase: message.data.round.phase,
          champion: message.data.round.champion,
          challenger: message.data.round.challenger,
          available_challenger_pool: [], // Not included in WebSocket message
          player_pick: message.data.round.playerPick,
          betting_start_time: undefined, // Not included in WebSocket message
          betting_end_time: message.data.round.bettingEndTime,
          race_start_time: message.data.round.raceStartTime,
          race_end_time: message.data.round.raceEndTime,
          starting_prices: message.data.round.startingPrices,
          ending_prices: message.data.round.endingPrices,
          race_winner: message.data.round.raceWinner,
          player_won: message.data.round.playerWon,
          player_decision: message.data.round.playerDecision,
          signed_race_details: message.data.round.signedRaceDetails,  // For on-chain betting
          signed_results: message.data.round.signedResults,  // For on-chain cash out
          // Round is completed if player lost (busted) or if in RESULTS with decision made
          completed_at: message.data.round.playerDecision === 'busted' ? new Date().toISOString() : undefined
        };
        
        setCurrentRound(roundUpdate);
      } else if (message.type !== 'session_ended') {
        // Only clear round if it's not a session_ended event
        // For session_ended, keep the existing round to show final results
        setCurrentRound(null);
      }

      // Handle specific event types
      switch (message.type) {
        case 'session_created':
          break;
        case 'asset_picked':
          break;
        case 'race_started':
          break;
        case 'settlement_started':
          break;
        case 'race_settled':
          // Store signed_results for potential continue_streak in next round
          if (message.data.round?.signedResults) {
            setPreviousRoundResults(message.data.round.signedResults);
          }
          break;
        case 'new_round':
          break;
        case 'session_ended':
          // Keep the session and round data to show final state (cashed_out or busted)
          // The session data was already updated above with the final session_status
          // Don't clear it - let the user see their results and "Play Again" button
          break;
        case 'session_expired':
          // Session expired (no bet placed on round 1)
          // Clear session state - user never actually started playing
          setCurrentSession(null);
          setCurrentRound(null);
          setError('Betting Timed Out');
          // Don't show error - just silently return to start screen
          break;
        case 'round_reset':
          // Round was reset - user clicked "Next Race" but didn't pick in time
          // Backend deleted the uncommitted round and reset the previous round's player_decision to null
          // The round data in message.data.round is the RESET round (previous round they won)
          setIsLoading(false);  // Clear any loading state
          setError('Betting timed out - try again');
          // Round state already updated above (lines 258-283)
          // Error will auto-dismiss after timeout (lines 42-50)
          break;
        case 'round_expired':
          setCurrentRound(null);
          setIsLoading(false);
          setError('Betting timed out - try again');
          break;
      }
    };

    // Add WebSocket callback
    bettingWebSocketService.addGameSessionCallback(handleGameSessionMessage);

    // Cleanup on unmount
    return () => {
      bettingWebSocketService.removeGameSessionCallback(handleGameSessionMessage);
    };
  }, [address]);

  /**
   * Check if user has an active session
   */
  const checkActiveSession = useCallback(async () => {
    if (!address) return;
    
    try {
      const result = await gameApiService.getActiveSession(address);
      
      if (result.success) {
        // Set session/round if we have session data (includes cashed_out sessions)
        if (result.data?.session) {
          setCurrentSession(result.data.session);
          const round = result.data.round;
          setCurrentRound(round);
          
          // Handle signed_results for continue_streak
          if (round) {
            if (round.signed_results) {
              // Current round has results (RESULTS phase) - store for next round
              console.log('💾 Storing signed_results from current round (RESULTS phase)');
              setPreviousRoundResults(round.signed_results);
            } else if (round.round_number > 1 && round.phase === 'BETTING') {
              // Round 2+ in BETTING phase - need to fetch previous round's results
              console.log('🔍 Fetching previous round results for continue_streak...');
              gameApiService.getRound(result.data.session.session_id, round.round_number - 1)
                .then(prevRoundResult => {
                  if (prevRoundResult.success && prevRoundResult.data?.round?.signed_results) {
                    console.log('💾 Stored previous round results');
                    setPreviousRoundResults(prevRoundResult.data.round.signed_results);
                  }
                });
            }
          }
        } else {
          // Only clear if truly no session (never played)
          setCurrentSession(null);
          setCurrentRound(null);
          setPreviousRoundResults(null);
        }
      } else {
        // Don't log error - backend might not be running yet
        console.log('Note: No active game session found');
      }
    } catch (err) {
      // Silently handle - game session backend might not be ready
      console.log('Note: Game session check skipped - backend not available');
      throw err; // Re-throw so caller can handle
    }
  }, [address]);

  // ✅ FIX #1: Refetch state when WebSocket reconnects (handles missed messages)
  useEffect(() => {
    const handleConnectionRestored = () => {
      // Refetch current session state to catch any missed messages
      if (address && isAuthenticated) {
        checkActiveSession().catch(err => {
          console.error('Failed to refetch session after reconnection:', err);
        });
      }
    };
    
    window.addEventListener('bettingConnectionRestored', handleConnectionRestored);
    
    return () => {
      window.removeEventListener('bettingConnectionRestored', handleConnectionRestored);
    };
  }, [address, isAuthenticated, checkActiveSession]);

  // Stuck state detection - poll for updates if we haven't received expected messages
  useEffect(() => {
    if (!currentRound || !address) return;
    
    // Check if we're stuck in BETTING phase past betting_end_time
    if (currentRound.phase === 'BETTING' && currentRound.betting_end_time) {
      const bettingEndTime = new Date(currentRound.betting_end_time).getTime();
      const now = Date.now();
      const msOverdue = now - bettingEndTime;
      
      // ✅ FIX #2: Reduced from 5s to 2s for faster recovery
      if (msOverdue > 2000) {
        console.warn('⚠️ Stuck in BETTING phase, polling for current state...');
        
        const pollTimer = setTimeout(() => {
          checkActiveSession().catch(err => {
            console.error('Failed to poll for stuck state:', err);
          });
        }, 1000); // Wait 1 more second then poll
        
        return () => clearTimeout(pollTimer);
      }
    }
    
    // Check if we're stuck in RACING phase past race_end_time
    if (currentRound.phase === 'RACING' && currentRound.race_end_time) {
      const raceEndTime = new Date(currentRound.race_end_time).getTime();
      const now = Date.now();
      const msOverdue = now - raceEndTime;
      
      // ✅ FIX #2: Reduced from 5s to 2s for faster recovery
      if (msOverdue > 2000) {
        console.warn('⚠️ Stuck in RACING phase, polling for current state...');
        
        const pollTimer = setTimeout(() => {
          checkActiveSession().catch(err => {
            console.error('Failed to poll for stuck state:', err);
          });
        }, 1000); // Wait 1 more second then poll
        
        return () => clearTimeout(pollTimer);
      }
    }
  }, [currentRound, address, checkActiveSession]);

  /**
   * Create a new game session
   */
  const createSession = useCallback(async (initialBet: number) => {
    if (!address) {
      setError('Wallet not connected');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await gameApiService.createSession(address, initialBet);
      
      if (!result.success) {
        setError(result.error || 'Failed to create session');
      }
      // State will be updated via WebSocket message
      
    } catch (err: any) {
      setError(err.message || 'Failed to create session');
    } finally {
      setIsLoading(false);
    }
  }, [address]);

  /**
   * Pick an asset during BETTING phase (with on-chain transaction)
   * Round 1: Uses placeBet
   * Round 2+: Uses continueStreak (settles previous round + places new bet in one transaction)
   */
  const pickAsset = useCallback(async (asset: string) => {
    // ✅ INSTANT loading state + transaction lock
    setIsLoading(true);
    setError(null);
    
    // ✅ Prevent double-clicks with transaction lock
    if (transactionInProgress) {
      console.warn('⚠️ Transaction already in progress, ignoring click');
      setIsLoading(false);
      return;
    }
    setTransactionInProgress(true);
    
    if (!address || !currentSession || !currentRound) {
      console.error('❌ No active session, round, or address');
      setError('No active session');
      setIsLoading(false);
      setTransactionInProgress(false);
      return;
    }
    
    if (!currentRound.signed_race_details) {
      console.error('❌ No signed race details available');
      setError('Race details not available');
      setIsLoading(false);
      setTransactionInProgress(false);
      return;
    }
    
    try {
      let txResult;
      
      // Determine which transaction to use based on round number
      if (currentRound.round_number === 1) {
        // Round 1: Simple place_bet
        txResult = await placeBetOnChain(
          currentRound.signed_race_details,
          asset,
          currentSession.initial_bet
        );
      } else {
        // Round 2+: continue_streak (settle + bet)
        
        // Use signed_results from previous round (stored when race_settled)
        if (!previousRoundResults) {
          throw new Error('Previous round results not available - cannot continue streak');
        }
        
        txResult = await continueStreakOnChain(
          previousRoundResults,  // Previous round results (from state)
          currentRound.signed_race_details,  // New round race details
          asset,
          currentSession.initial_bet
        );
      }
      
      if (!txResult.success || !txResult.signature) {
        throw new Error(txResult.error || 'Transaction failed');
      }
      
      // Step 2: Send transaction signature to backend for tracking
      const result = await gameApiService.pickAsset(
        currentSession.session_id,
        asset,
        txResult.signature,
        address
      );
      
      if (!result.success) {
        console.error('❌ Failed to record pick in backend:', result.error);
        setError(result.error || 'Failed to record pick');
      }
      // State will be updated via WebSocket message
      
    } catch (err: any) {
      console.error('❌ Exception picking asset:', err);
      
      // Make user-friendly error messages
      let errorMsg = err.message || 'Failed to pick asset';
      if (errorMsg.includes('User rejected')) {
        errorMsg = 'Transaction cancelled';
      }
      
      setError(errorMsg);
    } finally {
      setIsLoading(false);
      setTransactionInProgress(false); // ✅ Release lock
    }
  }, [address, currentSession, currentRound, previousRoundResults, placeBetOnChain, continueStreakOnChain, transactionInProgress]);

  /**
   * Cash out winnings during RESULTS phase (after winning)
   */
  const cashOut = useCallback(async () => {
    // ✅ INSTANT loading state + transaction lock
    setIsLoading(true);
    setError(null);
    
    // ✅ Prevent double-clicks with transaction lock
    if (transactionInProgress) {
      console.warn('⚠️ Transaction already in progress, ignoring click');
      setIsLoading(false);
      return;
    }
    setTransactionInProgress(true);
    
    if (!address || !currentSession || !currentRound) {
      setError('No active session or round');
      setIsLoading(false);
      setTransactionInProgress(false);
      return;
    }
    
    if (!currentRound.signed_results) {
      setError('Signed results not available');
      setIsLoading(false);
      setTransactionInProgress(false);
      return;
    }
    
    try {
      // 1. Send cash_out transaction on-chain
      const txResult = await cashOutOnChain(currentRound.signed_results);
      
      if (!txResult.success || !txResult.signature) {
        throw new Error(txResult.error || 'Failed to send cash out transaction');
      }
      
      // 2. Send transaction signature to backend for tracking
      const result = await gameApiService.cashOut(
        currentSession.session_id,
        address,
        txResult.signature
      );
      
      if (!result.success) {
        setError(result.error || 'Failed to cash out');
      }
      // State will be updated via WebSocket message
      
    } catch (err: any) {
      // Handle user rejection gracefully
      if (err.message?.includes('rejected') || err.message?.includes('cancelled')) {
        setError('Transaction cancelled');
      } else {
        setError(err.message || 'Failed to cash out');
      }
    } finally {
      setIsLoading(false);
      setTransactionInProgress(false); // ✅ Release lock
    }
  }, [address, currentSession, currentRound, cashOutOnChain, transactionInProgress]);

  /**
   * Continue to next round during RESULTS phase (after winning)
   */
  const continueGame = useCallback(async () => {
    // ✅ INSTANT loading state + transaction lock
    setIsLoading(true);
    setError(null);
    
    // ✅ Prevent double-clicks with transaction lock
    if (transactionInProgress) {
      console.warn('⚠️ Transaction already in progress, ignoring click');
      setIsLoading(false);
      return;
    }
    setTransactionInProgress(true);
    
    if (!address || !currentSession) {
      console.error('❌ No active session or address');
      setError('No active session');
      setIsLoading(false);
      setTransactionInProgress(false);
      return;
    }
    
    try {
      const result = await gameApiService.continue(currentSession.session_id, address);
      
      if (!result.success) {
        console.error('❌ Failed to continue:', result.error);
        setError(result.error || 'Failed to continue');
      }
      // State will be updated via WebSocket message
      
    } catch (err: any) {
      console.error('❌ Exception continuing:', err);
      setError(err.message || 'Failed to continue');
    } finally {
      setIsLoading(false);
      setTransactionInProgress(false); // ✅ Release lock
    }
  }, [address, currentSession, transactionInProgress]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear all session state (called on logout)
   */
  const clearSessionState = useCallback(() => {
    setCurrentSession(null);
    setCurrentRound(null);
    setPreviousRoundResults(null);
    setError(null);
    setMatchupHistory(null);
    setIsLoading(false);
    // Keep sessionCheckComplete as true to show demo mode
    setSessionCheckComplete(true);
  }, []);

  /**
   * Compute race matchup for display (works for both demo and session modes)
   */
  const raceMatchup = useMemo((): RaceMatchup | null => {
    // Case 1: Active session - use champion vs challenger from current round
    if (currentRound) {
      // In RESULTS phase, use frozen ending prices from settlement
      // Otherwise use live prices from WebSocket
      const useEndingPrices = currentRound.phase === 'RESULTS' && currentRound.ending_prices;
      
      const championPrice = useEndingPrices 
        ? currentRound.ending_prices![currentRound.champion] 
        : (allPrices[currentRound.champion]?.price || 0);
      const challengerPrice = useEndingPrices 
        ? currentRound.ending_prices![currentRound.challenger]
        : (allPrices[currentRound.challenger]?.price || 0);
      
      let championPercentGain = 0;
      let challengerPercentGain = 0;
      
      // Calculate % gains if race is running (has starting prices)
      if (currentRound.starting_prices) {
        const champStart = currentRound.starting_prices[currentRound.champion];
        const challStart = currentRound.starting_prices[currentRound.challenger];
        
        if (champStart && challStart) {
          championPercentGain = ((championPrice - champStart) / champStart) * 100;
          challengerPercentGain = ((challengerPrice - challStart) / challStart) * 100;
        }
      }
            
      return {
        champion: currentRound.champion,
        challenger: currentRound.challenger,
        championPrice,
        challengerPrice,
        championPercentGain,
        challengerPercentGain,
        startingPrices: currentRound.starting_prices ? {
          champion: currentRound.starting_prices[currentRound.champion],
          challenger: currentRound.starting_prices[currentRound.challenger]
        } : undefined,
        isLive: currentRound.phase === 'RACING'
      };
    }
    
    // Case 2: Demo mode - show default matchup (BTC vs SOL) with LIVE price movements
    const availableAssets = Object.keys(allPrices);
    if (availableAssets.length >= 2 && demoBaseline) {
      // Use BTC vs SOL as default, or first two available assets
      const champion = availableAssets.includes('BTC') ? 'BTC' : availableAssets[0];
      const challenger = availableAssets.includes('SOL') ? 'SOL' : availableAssets[1];
      
      // Get current and baseline prices
      const champCurrentPrice = allPrices[champion]?.price || 0;
      const challCurrentPrice = allPrices[challenger]?.price || 0;
      const champBasePrice = demoBaseline.prices[champion] || champCurrentPrice;
      const challBasePrice = demoBaseline.prices[challenger] || challCurrentPrice;
      
      // ✅ Calculate live % changes from baseline
      const championPercentGain = champBasePrice > 0 
        ? ((champCurrentPrice - champBasePrice) / champBasePrice) * 100 
        : 0;
      const challengerPercentGain = challBasePrice > 0
        ? ((challCurrentPrice - challBasePrice) / challBasePrice) * 100
        : 0;
      
      return {
        champion,
        challenger,
        championPrice: champCurrentPrice,
        challengerPrice: challCurrentPrice,
        championPercentGain, // ✅ Now shows LIVE changes from baseline!
        challengerPercentGain,
        startingPrices: {
          champion: champBasePrice,
          challenger: challBasePrice
        },
        isLive: false // Still demo, but with live price movements
      };
    }
    
    // No data yet or waiting for baseline
    return null;
  }, [currentRound, allPrices, demoBaseline]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    currentSession,
    currentRound,
    payoutLadder,
    matchupHistory,
    recentResults,
    isLoading,
    sessionCheckComplete,
    error,
    raceMatchup,
    allPrices,
    createSession,
    pickAsset,
    cashOut,
    continueGame,
    checkActiveSession,
    clearError,
    clearSessionState
  }), [
    currentSession,
    currentRound,
    payoutLadder,
    matchupHistory,
    recentResults,
    isLoading,
    sessionCheckComplete,
    error,
    raceMatchup,
    allPrices,
    createSession,
    pickAsset,
    cashOut,
    continueGame,
    checkActiveSession,
    clearError,
    clearSessionState
  ]);

  return (
    <GameSessionContext.Provider value={contextValue}>
      {children}
    </GameSessionContext.Provider>
  );
};

/**
 * Hook to access game session context
 */
export const useGameSession = () => {
  const context = useContext(GameSessionContext);
  
  if (!context) {
    throw new Error('useGameSession must be used within GameSessionProvider');
  }
  
  return context;
};

// Export types for convenience
export type { GameSession, GameRound, PayoutLadder, RaceMatchup };

